<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'

import { useCalculationStore } from '@/stores/calculation'
import { useProjectStore } from '@/stores/project'

import ResultTitle from '@/components/calculation-result/ResultTitle.vue'
import ResultFilesTable from '@/components/calculation-result/ResultFilesTable.vue'
import ComputeResourcesTable from '@/components/calculation-result/ComputeResourcesTable.vue'

const route = useRoute()
const router = useRouter()

const calculationStore = useCalculationStore()
const projectStore = useProjectStore()

const { currentItem } = storeToRefs(calculationStore)
const { spending, spendingLoading } = storeToRefs(projectStore)

const calculationId = computed(() => parseInt(route.params.calculationId as string))
const AUTO_REFRESH_INTERVAL_MS = 60000
const TERMINAL_CALCULATION_STATUSES = new Set(['DONE', 'ERROR', 'TIMEOUT', 'CANCELLED'])

const loading = ref(true)
const currentTab = ref('compute')
let calculationIntervalId: number | undefined

const tabs = computed(() => [
  {
    value: 'compute',
    label: 'Virtual machines'
  },
  {
    value: 'output-files',
    label: 'Output files'
  }
])

const validTabs = computed(() => new Set(tabs.value.map((tab) => tab.value)))

const normalizeTab = (value: unknown) => {
  if (typeof value !== 'string') return 'compute'
  return validTabs.value.has(value) ? value : 'compute'
}

const routeTab = computed(() => normalizeTab(route.query.tab))

const refreshCalculation = async () => {
  if (!calculationId.value) return
  await calculationStore.fetchItem(calculationId.value)

  if (isTerminalCalculation()) {
    await refreshOutputFiles()
    stopAutoRefresh()
    return
  }

  await refreshOutputFiles()
}

const refreshOutputFiles = async () => {
  if (currentItem.value?.id) {
    await calculationStore.getResultFiles(currentItem.value.id)
  }
}

const isTerminalCalculation = () => {
  return TERMINAL_CALCULATION_STATUSES.has(String(currentItem.value?.status ?? ''))
}

const spendingCapExceeded = computed(() => Boolean(spending.value?.limit_exceeded))
const spendingLoaded = computed(() => Boolean(spending.value))

const startAutoRefresh = () => {
  stopAutoRefresh()
  if (isTerminalCalculation()) return

  calculationIntervalId = window.setInterval(refreshCalculation, AUTO_REFRESH_INTERVAL_MS)
}

const stopAutoRefresh = () => {
  if (calculationIntervalId) {
    clearInterval(calculationIntervalId)
    calculationIntervalId = undefined
  }
}

const handleRescheduled = async () => {
  await refreshCalculation()
  startAutoRefresh()
}

const handleStopped = async () => {
  stopAutoRefresh()
  await refreshCalculation()
}

watch(
  routeTab,
  (value) => {
    if (value !== currentTab.value) {
      currentTab.value = value
    }
  },
  { immediate: true }
)

watch(currentTab, async (value) => {
  const normalized = normalizeTab(value)
  if (normalized !== value) {
    currentTab.value = normalized
    return
  }
  if (normalized === normalizeTab(route.query.tab)) return
  await router.replace({
    query: {
      ...route.query,
      tab: normalized
    }
  })
})

onMounted(async () => {
  try {
    await calculationStore.fetchOptions()
    await refreshCalculation()

    if (currentItem.value?.project) {
      const { project } = currentItem.value
      await projectStore.fetchOptions()
      await projectStore.fetchItem(project)
      await projectStore.fetchSpending(project)
    }

    startAutoRefresh()
  } finally {
    loading.value = false
  }
})

onUnmounted(() => {
  stopAutoRefresh()
})
</script>

<template>
  <template v-if="loading && !currentItem">
    <div class="d-flex justify-center align-center fill-height">
      <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
    </div>
  </template>
  <div v-else style="display: flex; flex-direction: column" class="fill-height">
    <ResultTitle
      v-if="currentItem"
      :spending-cap-exceeded="spendingCapExceeded"
      :spending-loaded="spendingLoaded"
      :spending-loading="spendingLoading"
      @rescheduled="handleRescheduled"
      @stopped="handleStopped"
    />
    <v-card
      v-if="currentItem"
      class="calculation-tabs mx-2 mt-4 w-100"
      color="#F6F6F6"
      flat
      rounded="lg"
    >
      <v-tabs v-model="currentTab" class="calculation-tabs-nav" hide-slider>
        <v-tab v-for="tab in tabs" :key="tab.value" :value="tab.value" class="calculation-tab">
          {{ tab.label }}
        </v-tab>
      </v-tabs>

      <v-window v-model="currentTab" class="calculation-tabs-window">
        <v-window-item value="compute">
          <div class="tab-content-surface">
            <ComputeResourcesTable />
          </div>
        </v-window-item>
        <v-window-item value="output-files">
          <div class="tab-content-surface">
            <ResultFilesTable />
          </div>
        </v-window-item>
      </v-window>
    </v-card>
  </div>
</template>

<style scoped>
.calculation-tabs {
  background-color: #f6f6f6 !important;
}

.calculation-tabs-nav {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.2);
}

.calculation-tabs-window {
  overflow: visible !important;
}

.calculation-tab.v-tab.v-btn {
  border-bottom: 6px solid transparent;
  border-radius: 0 !important;
  color: rgb(var(--v-theme-primary)) !important;
  display: flex;
  align-items: center;
  font-weight: 600;
  min-height: 50px;
  padding-left: 24px;
  padding-right: 24px;
  text-transform: none !important;
}

.calculation-tab.v-tab--selected {
  background-color: rgba(var(--v-theme-primary), 0.16) !important;
  border-bottom-color: rgb(var(--v-theme-primary)) !important;
  color: rgb(var(--v-theme-on-surface)) !important;
}

.calculation-tab:hover:not(.v-tab--selected):not(.v-tab--disabled) {
  background-color: rgba(var(--v-theme-primary), 0.04) !important;
}

.calculation-tab:deep(.v-btn__content) {
  display: inline-flex;
  align-items: center;
}

.tab-content-surface {
  background: #f6f6f6;
  overflow: visible;
  padding: 20px;
}

.calculation-tabs-window,
.calculation-tabs-window :deep(.v-window__container),
.calculation-tabs-window :deep(.v-window-item) {
  overflow: visible;
}

.tab-content-surface :deep(.v-card.mx-2.mt-4) {
  margin: 0 !important;
}
</style>
